import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { ListNestedUser, UserProfile } from '@sendhub/api-client-typescript-axios'
import lodash from 'lodash'
import { sendhubApi as sh } from '../../services/sendhub-api'
import { RootState } from '../root'
import { INTEGRATIONS_BASE_URL, SENDHUB_BASE_URL } from '../../../utils/config'
import { sendhubSessionKeyHeader } from '../../../utils/session-key'
import { bearerTokenHeader } from '../../../utils/token-store'

console.log('process.env:', process.env)
console.log("process.env['NODE_ENV']:", process.env['NODE_ENV'])

export const SENDHUB_USERS_FEATURE_KEY = 'sendhubUsers'

export interface SubUserModulePermission {
  module: string
  level: string
}

function parseSubUserPermissions(data: Record<string, unknown> | undefined): SubUserModulePermission[] {
  const raw = data?.['subUserPermissions'] || data?.['sub_user_permissions']
  if (!Array.isArray(raw)) return []
  return raw.filter((row): row is SubUserModulePermission => !!row && typeof row.module === 'string' && typeof row.level === 'string')
}

/** Sub-users cannot call GET /v1/enterprises/:id/users (enterprise admin). */
export function shouldSkipEnterpriseUserList(
  permissions: SubUserModulePermission[] | undefined,
  profile?: Record<string, unknown>
): boolean {
  if ((permissions || []).length > 0) return true
  const owner = profile?.['ownerUserId'] ?? profile?.['owner_user_id']
  if (owner != null && String(owner) !== '') return true
  const isSubUser = (
    window as unknown as {
      SH?: { models?: { UserProfileSingleton?: { isSubUser?: () => boolean } } }
    }
  ).SH?.models?.UserProfileSingleton?.isSubUser
  if (typeof isSubUser === 'function' && isSubUser()) return true
  return false
}

export interface UsersState extends EntityState<Readonly<ListNestedUser>> {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  error?: string | null
  profile?: UserProfile
  subUserPermissions?: SubUserModulePermission[]
}

export const sendhubUsersAdapter = createEntityAdapter<Readonly<ListNestedUser>>({ selectId: (user) => user.user_id })

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(fetchUsers())
 * }, [dispatch]);
 * ```
 */
export const fetchUsers = createAsyncThunk('sendhubUsers/fetchStatus', async (_, thunkAPI) => {
  let skipListUsers = shouldSkipEnterpriseUserList(undefined)
  try {
    const v2ProfileResp = await fetch(`${INTEGRATIONS_BASE_URL}/proxy/${SENDHUB_BASE_URL}/api/v2/profile/`, {
      credentials: 'include',
      // Same credentials as the typed client: see utils/session-key.ts and
      // utils/token-store.ts for why cookies alone can't be relied on.
      headers: { ...sendhubSessionKeyHeader(), ...bearerTokenHeader() },
    })
    if (v2ProfileResp.ok) {
      const v2Profile = (await v2ProfileResp.json()) as Record<string, unknown>
      const permissions = parseSubUserPermissions(v2Profile)
      thunkAPI.dispatch(sendhubUsersActions.setSubUserPermissions(permissions))
      skipListUsers = shouldSkipEnterpriseUserList(permissions, v2Profile)
    }
  } catch (error) {
    console.error('Failed to fetch v2 profile permissions', error)
  }

  sh.api.profile
    .getProfile()
    .then((profileResp) => {
      thunkAPI.dispatch(sendhubUsersActions.setProfile(profileResp.data))
      if (skipListUsers) return
      sh.api.profile.listUsers(profileResp.data.enterprise_id).then((usersResp) => {
        if (usersResp.data.objects === undefined) return
        thunkAPI.dispatch(sendhubUsersSlice.actions.setMany(usersResp.data.objects))
      })
    })
    .catch((error) => {
      console.error('Failed to fetch SendHub users', error)
      return thunkAPI.rejectWithValue('Failed to fetch SendHub users')
    })
})

export const initialUsersState: UsersState = sendhubUsersAdapter.getInitialState({
  loadingStatus: 'not loaded',
  error: null,
})

export const sendhubUsersSlice = createSlice({
  name: SENDHUB_USERS_FEATURE_KEY,
  initialState: initialUsersState,
  reducers: {
    add: sendhubUsersAdapter.addOne,
    setMany: sendhubUsersAdapter.setMany,
    remove: sendhubUsersAdapter.removeOne,
    setProfile(state, action: PayloadAction<UserProfile>) {
      state.profile = action.payload
    },
    setSubUserPermissions(state, action: PayloadAction<SubUserModulePermission[]>) {
      state.subUserPermissions = action.payload
    },
    // ...
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state: UsersState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(fetchUsers.fulfilled, (state: UsersState /** , action: PayloadAction<ListNestedUser[] | undefined> */) => {
        // if (action.payload) sendhubUsersAdapter.setAll(state, action.payload)
        state.loadingStatus = 'loaded'
      })
      .addCase(fetchUsers.rejected, (state: UsersState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })
  },
})

/*
 * Export reducer for store configuration.
 */
export const sendhubUsersReducer = sendhubUsersSlice.reducer

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(sendhubUsersActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const sendhubUsersActions = sendhubUsersSlice.actions

/*
 * Export selectors to query state. For use with the `useSelector` hook.
 *
 * e.g.
 * ```
 * import { useSelector } from 'react-redux';
 *
 * // ...
 *
 * const entities = useSelector(selectAllUsers);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#useselector
 */
const { selectAll, selectEntities, selectIds } = sendhubUsersAdapter.getSelectors()

export const getUsersState = (rootState: RootState): UsersState => rootState[SENDHUB_USERS_FEATURE_KEY]

export const selectAllUsers = createSelector(getUsersState, selectAll)

export const selectUsersEntities = createSelector(getUsersState, selectEntities)
export const selectUsersLoadingStatus = createSelector(getUsersState, (state) => state.loadingStatus)
export const selectUsersError = createSelector(getUsersState, (state) => state.error)
export const selectUsersIds = createSelector(getUsersState, (state) => state.ids)
export const selectProfile = createSelector(getUsersState, (state) => state.profile)
